import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO, isToday, isTomorrow, isPast } from "date-fns";
import { Loader2, Phone, MapPin, User, ShieldOff, RefreshCw, ChefHat, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { cn } from "../lib/utils";

const STATUS_META = {
  new:              { label: "New",             color: "bg-[#D02030]/15 text-[#D02030]" },
  preparing:        { label: "Preparing",       color: "bg-[#E29A26]/20 text-[#B57516]" },
  ready:            { label: "Ready for pickup",color: "bg-[#1A6B2E]/15 text-[#1A6B2E]" },
  out_for_delivery: { label: "Out for delivery",color: "bg-[#1A6B2E]/15 text-[#1A6B2E]" },
  completed:        { label: "Completed",       color: "bg-[#4E5C4F]/15 text-[#4E5C4F]" },
  cancelled:        { label: "Cancelled",       color: "bg-[#4E5C4F]/15 text-[#4E5C4F] line-through" },
};

const STATUS_ORDER = ["new", "preparing", "ready", "out_for_delivery", "completed", "cancelled"];

function bucketLabel(dateStr) {
  try {
    const d = parseISO(dateStr);
    if (isToday(d)) return "Today";
    if (isTomorrow(d)) return "Tomorrow";
    if (isPast(d)) return "Past · " + format(d, "EEE, MMM d");
    return format(d, "EEE, MMM d");
  } catch {
    return dateStr;
  }
}

export default function AdminOrders() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | all
  const [refreshing, setRefreshing] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  const previewDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.get("/admin/digest/preview");
      setDigestPreview(data);
      setDigestOpen(true);
    } catch {
      toast.error("Could not load digest preview.");
    } finally {
      setDigestBusy(false);
    }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      if (data.status === "sent") {
        toast.success(`Digest sent to ${data.to}`);
      } else if (data.status === "mocked") {
        toast.info("Digest generated (MOCKED)", {
          description: `Would have emailed ${data.to}. Add RESEND_API_KEY to enable real send.`,
        });
      } else {
        toast.error(`Digest not sent: ${data.reason || data.error || "unknown"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed.");
    } finally {
      setDigestBusy(false);
    }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data.orders);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Your Google account isn't set as an admin. Ask the developer to add your email to ADMIN_EMAILS.");
      } else if (err.response?.status === 401) {
        setError("Please sign in with Google first.");
      } else {
        setError("Could not load orders.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    load();
    // Auto-refresh every 30s so the kitchen sees new orders live
    const iv = setInterval(() => { setRefreshing(true); load(true); }, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const updateStatus = async (order, next) => {
    const prev = order.kitchen_status || "new";
    setOrders((os) => os.map((o) => (o.order_id === order.order_id ? { ...o, kitchen_status: next } : o)));
    try {
      await api.patch(`/admin/orders/${order.order_id}/status`, { kitchen_status: next });
      toast.success(`${order.order_id.slice(-6).toUpperCase()} → ${STATUS_META[next].label}`);
    } catch {
      setOrders((os) => os.map((o) => (o.order_id === order.order_id ? { ...o, kitchen_status: prev } : o)));
      toast.error("Could not update status.");
    }
  };

  // Filter + bucket
  const visible = useMemo(() => {
    const active = ["new", "preparing", "ready", "out_for_delivery"];
    if (statusFilter === "active") return orders.filter((o) => active.includes(o.kitchen_status || "new"));
    return orders;
  }, [orders, statusFilter]);

  const buckets = useMemo(() => {
    const map = new Map();
    for (const o of visible) {
      const key = o.delivery_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [visible]);

  if (authLoading || loading) {
    return <main className="max-w-6xl mx-auto px-6 py-24 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#1A6B2E]" /></main>;
  }
  if (!user) {
    return (
      <main data-testid="admin-orders-signin" className="max-w-md mx-auto px-6 py-24 text-center">
        <ShieldOff className="w-10 h-10 mx-auto text-[#D02030]" />
        <h1 className="font-display text-4xl mt-4 tracking-tighter">Admin sign in</h1>
        <p className="text-[#4E5C4F] mt-2">Sign in with the Google account registered as owner.</p>
        <button onClick={login} className="btn-primary rounded-full px-6 py-3 mt-8 text-sm font-semibold">Sign in with Google</button>
      </main>
    );
  }
  if (error) {
    return (
      <main data-testid="admin-orders-forbidden" className="max-w-md mx-auto px-6 py-24 text-center">
        <ShieldOff className="w-10 h-10 mx-auto text-[#D02030]" />
        <h1 className="font-display text-3xl mt-4 tracking-tighter">Not authorised</h1>
        <p className="text-[#4E5C4F] mt-2">{error}</p>
        <Link to="/" className="inline-block mt-6 text-sm underline">← Back to homepage</Link>
      </main>
    );
  }

  return (
    <main data-testid="admin-orders-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <span className="font-label text-xs text-[#D02030]">Kitchen · Orders</span>
          <h1 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">Today's board.</h1>
          <p className="text-sm text-[#4E5C4F] mt-2">
            Only paid orders show here. Move them through the kitchen with one tap. Auto-refreshes every 30s.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/admin"
            className="text-sm font-medium rounded-full border border-[#1A6B2E]/25 px-4 py-2 hover:bg-[#F4EFEB] inline-flex items-center gap-2"
          >
            <ChefHat className="w-4 h-4" /> Menu
          </Link>
          <button
            data-testid="digest-preview-btn"
            onClick={previewDigest}
            disabled={digestBusy}
            className="text-sm font-medium rounded-full border border-[#1A6B2E]/25 px-4 py-2 hover:bg-[#F4EFEB] inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Mail className="w-4 h-4" /> Digest
          </button>
          <button
            data-testid="orders-refresh-btn"
            onClick={() => { setRefreshing(true); load(true); }}
            className="text-sm font-medium rounded-full border border-[#1A6B2E]/25 px-4 py-2 hover:bg-[#F4EFEB] inline-flex items-center gap-2"
          >
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} /> Refresh
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="filter-active-btn"
              onClick={() => setStatusFilter("active")}
              className={cn("rounded-full px-4 py-2 text-sm font-medium border transition-colors",
                statusFilter === "active" ? "bg-[#1A6B2E] text-white border-[#1A6B2E]" : "bg-white border-[#1A6B2E]/20 hover:bg-[#F4EFEB]")}
            >
              Active
            </button>
            <button
              data-testid="filter-all-btn"
              onClick={() => setStatusFilter("all")}
              className={cn("rounded-full px-4 py-2 text-sm font-medium border transition-colors",
                statusFilter === "all" ? "bg-[#1A6B2E] text-white border-[#1A6B2E]" : "bg-white border-[#1A6B2E]/20 hover:bg-[#F4EFEB]")}
            >
              All
            </button>
          </div>
        </div>
      </div>

      {buckets.length === 0 ? (
        <div className="chop-card p-10 text-center">
          <p className="text-[#4E5C4F]">No {statusFilter === "active" ? "active" : ""} orders yet. When customers pay, they'll appear here.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {buckets.map(([dateKey, rows]) => (
            <section key={dateKey} data-testid={`bucket-${dateKey}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-2xl">{bucketLabel(dateKey)}</h2>
                <span className="text-xs text-[#4E5C4F]">{rows.length} order{rows.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rows.map((o) => {
                  const isPickup = (o.city || "").toLowerCase().includes("pickup");
                  const status = o.kitchen_status || "new";
                  return (
                    <div
                      key={o.order_id}
                      data-testid={`order-card-${o.order_id}`}
                      className="chop-card chop-shadow p-5 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-label text-[10px] text-[#4E5C4F]">
                            {o.order_id.toUpperCase()}
                          </p>
                          <p className="font-display text-xl leading-tight mt-0.5">
                            {isPickup ? "Pickup" : "Delivery"} · {o.time_slot}
                          </p>
                        </div>
                        <span data-testid={`status-badge-${o.order_id}`} className={cn("rounded-full px-3 py-1 text-[10px] font-label whitespace-nowrap", STATUS_META[status]?.color)}>
                          {STATUS_META[status]?.label || status}
                        </span>
                      </div>

                      <div className="space-y-1 text-sm">
                        <p className="flex items-center gap-2 text-[#0E2B13]"><User className="w-3.5 h-3.5 text-[#1A6B2E]" /> {o.customer_name}</p>
                        <p className="flex items-center gap-2 text-[#0E2B13]">
                          <Phone className="w-3.5 h-3.5 text-[#1A6B2E]" />
                          <a href={`tel:${o.customer_phone}`} className="hover:underline">{o.customer_phone}</a>
                        </p>
                        <p className="flex items-start gap-2 text-[#0E2B13]">
                          <MapPin className="w-3.5 h-3.5 text-[#1A6B2E] mt-0.5 shrink-0" />
                          <span className="min-w-0">
                            <span className="font-medium">{o.city}</span>
                            {!isPickup && <><br /><span className="text-[#4E5C4F]">{o.address}</span></>}
                          </span>
                        </p>
                      </div>

                      <div className="pt-2 border-t border-[#1A6B2E]/10 text-sm space-y-1">
                        {(o.items || []).map((it, i) => (
                          <div key={i} className="flex justify-between">
                            <span><span className="font-semibold">{it.quantity}×</span> {it.name}</span>
                            <span className="text-[#4E5C4F]">${(it.price * it.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                        {o.notes && (
                          <p className="mt-2 text-xs italic text-[#B57516] bg-[#E29A26]/10 rounded p-2">
                            📝 {o.notes}
                          </p>
                        )}
                      </div>

                      <div className="pt-2 border-t border-[#1A6B2E]/10 flex items-center justify-between gap-3">
                        <div className="text-xs text-[#4E5C4F]">
                          {o.promo_code && <span className="mr-2">🎟 {o.promo_code}</span>}
                          <span className="font-display text-base text-[#0E2B13]">${o.total.toFixed(2)}</span>
                        </div>
                        <div className="flex-1 max-w-[180px]">
                          <Select value={status} onValueChange={(v) => updateStatus(o, v)}>
                            <SelectTrigger data-testid={`status-select-${o.order_id}`} className="h-9 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_ORDER.map((s) => (
                                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="digest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter">
              Daily kitchen digest — preview
            </DialogTitle>
          </DialogHeader>
          {digestPreview && (
            <>
              <div className="text-sm text-[#4E5C4F] space-y-1 pb-3 border-b border-[#1A6B2E]/10">
                <p><b className="text-[#0E2B13]">To:</b> {digestPreview.to || "—"}</p>
                <p><b className="text-[#0E2B13]">Date:</b> {digestPreview.date}
                   <span className="mx-2">·</span>
                   <b className="text-[#0E2B13]">{digestPreview.order_count}</b> paid order{digestPreview.order_count === 1 ? "" : "s"}</p>
                <p>
                  <b className="text-[#0E2B13]">Mode:</b>{" "}
                  {digestPreview.mode === "live" ? (
                    <span className="text-[#1A6B2E]">Live (Resend)</span>
                  ) : (
                    <span className="text-[#B57516]">MOCKED — RESEND_API_KEY not set</span>
                  )}
                </p>
                <p className="text-xs">Sends automatically at 7:00 AM America/Toronto.</p>
              </div>
              <iframe
                data-testid="digest-preview-frame"
                title="Digest preview"
                srcDoc={digestPreview.html}
                className="flex-1 w-full rounded-lg border border-[#1A6B2E]/10 min-h-[400px] bg-[#FDFBF7]"
              />
              <div className="flex justify-end gap-3 pt-3">
                <button
                  onClick={() => setDigestOpen(false)}
                  className="text-sm rounded-full border border-[#1A6B2E]/25 px-5 py-2 hover:bg-[#F4EFEB]"
                >
                  Close
                </button>
                <button
                  data-testid="digest-send-btn"
                  onClick={sendDigestNow}
                  disabled={digestBusy}
                  className="btn-primary rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {digestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Send now
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
